// Copyright ©️ 2026 eVolve MEP, LLC

import type { StrongOmit } from '@evolvemep/api-ts';
import * as THREE from 'three';

import { disposeGroup } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import {
  HighlightableGroup,
  type HighlightableObjectName,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import { MarkedMeasurementLineGeometry } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/MarkedMeasurementLineGeometry';
import {
  UnmarkedMeasurementLineGeometry,
  type UnmarkedMeasurementLineGeometryOpts,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/UnmarkedMeasurementLineGeometry';

export type MeasurementGeometryOpts = StrongOmit<UnmarkedMeasurementLineGeometryOpts, 'lineType'>;

/**
 * A line for a length measurement on a bend
 */
export class MeasurementGeometry extends HighlightableGroup {
  public perspectiveLine: MarkedMeasurementLineGeometry;
  public orthoLine: UnmarkedMeasurementLineGeometry;
  constructor(highlightableObjectName: HighlightableObjectName, opts: MeasurementGeometryOpts) {
    super(highlightableObjectName);
    this.perspectiveLine = new MarkedMeasurementLineGeometry(highlightableObjectName, {
      ...opts,
      lineType: 'dimensionLine',
      markerType: 'arrow',
    });
    this.orthoLine = new UnmarkedMeasurementLineGeometry(highlightableObjectName, {
      ...opts,
      lineType: 'dimensionLine',
    });
    this.add(this.perspectiveLine);
    this.add(this.orthoLine);
  }

  toggleLinesForCamera(camera: THREE.Camera) {
    if (camera instanceof THREE.PerspectiveCamera) {
      this.perspectiveLine.unhide();
      this.orthoLine.hide();
    } else {
      this.perspectiveLine.hide();
      this.orthoLine.unhide();
    }
  }

  scaleForCamera(camera: THREE.Camera) {
    this.perspectiveLine.scaleForCamera(camera);
  }

  highlight() {
    super.highlight();
    this.perspectiveLine.highlight();
    this.orthoLine.highlight();
  }

  unhighlight() {
    super.unhighlight();
    this.perspectiveLine.unhighlight();
    this.orthoLine.unhighlight();
  }

  dispose() {
    this.perspectiveLine.dispose();
    this.orthoLine.dispose();
    this.remove(this.perspectiveLine);
    this.remove(this.orthoLine);
    disposeGroup(this);
  }
}
